import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
// material
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
// components
import BaseDialog from '../base-dialog';
import { useNotificationsProvider } from '../../context/NotificationsContext';
import { setup2FA, enable2FA, disable2FA, get2FAStatus } from './actions';

TwoFactorAuthDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default function TwoFactorAuthDialog({ open, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotificationsProvider();

  const [loading, setLoading] = useState(false);
  // 'check' | 'setup' | 'enabled' — Cognito has no recovery-code lifecycle,
  // so the legacy 'regenerate' / 'show-new-codes' steps are gone.
  const [step, setStep] = useState('check');

  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  // Setup data
  const [otpauthUri, setOtpauthUri] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verificationToken, setVerificationToken] = useState('');

  const resetState = () => {
    setLoading(false);
    setStep('check');
    setOtpauthUri(null);
    setSecret(null);
    setVerificationToken('');
  };

  const onCloseDialog = () => {
    resetState();
    onClose();
  };

  const check2FAStatus = () => {
    setLoading(true);
    dispatch(get2FAStatus(
      (data) => {
        setIs2FAEnabled(data.enabled);
        setStep(data.enabled ? 'enabled' : 'check');
        setLoading(false);
      },
      () => {
        showNotification('error', t('2fa.status-error', 'Failed to check 2FA status'));
        setLoading(false);
      }
    ));
  };

  const handleSetup = () => {
    setLoading(true);
    dispatch(setup2FA(
      (data) => {
        setOtpauthUri(data.otpauthUri);
        setSecret(data.secret);
        setStep('setup');
        setLoading(false);
      },
      () => {
        showNotification('error', t('2fa.setup-error', 'Failed to setup 2FA'));
        setLoading(false);
      }
    ));
  };

  const handleEnable = () => {
    if (verificationToken.length !== 6) {
      showNotification('error', t('2fa.invalid-token', 'Please enter a 6-digit code'));
      return;
    }

    setLoading(true);
    dispatch(enable2FA(
      verificationToken,
      () => {
        showNotification('success', t('2fa.enabled-success', '2FA enabled successfully'));
        check2FAStatus();
        setVerificationToken('');
      },
      () => {
        showNotification('error', t('2fa.enable-error', 'Invalid code. Please try again.'));
        setLoading(false);
      }
    ));
  };

  const handleDisable = () => {
    setLoading(true);
    dispatch(disable2FA(
      null,
      () => {
        showNotification('success', t('2fa.disabled-success', '2FA disabled successfully'));
        check2FAStatus();
      },
      () => {
        showNotification('error', t('2fa.disable-error', 'Failed to disable 2FA.'));
        setLoading(false);
      }
    ));
  };

  useEffect(() => {
    if (open) {
      check2FAStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const renderCheckStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Typography variant="body1">
          {t('2fa.description', 'Two-factor authentication adds an extra layer of security to your account. You will need to enter a code from your authenticator app when logging in.')}
        </Typography>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSetup}
          disabled={loading}
        >
          {t('2fa.enable-button', 'Enable Two-Factor Authentication')}
        </Button>
      </Grid>
    </Grid>
  );

  const renderSetupStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Typography variant="h6">
          {t('2fa.scan-qr', 'Step 1: Scan QR Code')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('2fa.scan-description', 'Use Google Authenticator, Microsoft Authenticator, or any TOTP app to scan this QR code:')}
        </Typography>
      </Grid>
      {otpauthUri && (
        <Grid item container justifyContent="center">
          <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: 'white' }}>
            <QRCodeSVG value={otpauthUri} size={200} level="M" />
          </Box>
        </Grid>
      )}
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          {t('2fa.manual-entry', 'Or enter this code manually:')}
        </Typography>
        <TextField
          value={secret || ''}
          fullWidth
          InputProps={{ readOnly: true }}
          size="small"
          sx={{ mt: 1, fontFamily: 'monospace' }}
        />
      </Grid>

      <Grid item>
        <Divider />
      </Grid>

      <Grid item>
        <Typography variant="h6">
          {t('2fa.verify', 'Step 2: Verify')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('2fa.verify-description', 'Enter the 6-digit code from your authenticator app:')}
        </Typography>
      </Grid>
      <Grid item>
        <TextField
          value={verificationToken}
          onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          fullWidth
          inputProps={{
            maxLength: 6,
            style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
          }}
        />
      </Grid>
      <Grid item>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              resetState();
              check2FAStatus();
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleEnable}
            disabled={loading || verificationToken.length !== 6}
          >
            {t('2fa.enable-confirm', 'Enable 2FA')}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );

  const renderEnabledStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Alert severity="success" icon={<CheckCircle />}>
          {t('2fa.enabled-message', 'Two-factor authentication is enabled on your account')}
        </Alert>
      </Grid>
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          {t('2fa.lockout-help', 'Lost access to your authenticator app? Use the "Reset 2FA" link on the login page to receive a recovery email.')}
        </Typography>
      </Grid>
      <Grid item>
        <Divider />
      </Grid>
      <Grid item>
        <Typography variant="h6" color="error">
          {t('2fa.disable-title', 'Disable Two-Factor Authentication')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('2fa.disable-description-cognito', 'This removes the TOTP requirement from your account. You can re-enable it at any time.')}
        </Typography>
      </Grid>
      <Grid item>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={handleDisable}
          disabled={loading}
        >
          {t('2fa.disable-confirm', 'Disable 2FA')}
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      title={t('2fa.title', 'Two-Factor Authentication')}
      loading={loading}
      hideActions
    >
      <Box sx={{ mt: 2 }}>
        {step === 'check' && !is2FAEnabled && renderCheckStep()}
        {step === 'setup' && renderSetupStep()}
        {step === 'enabled' && is2FAEnabled && renderEnabledStep()}
      </Box>
    </BaseDialog>
  );
}
